import { Match, Switch, type Component } from "solid-js";
import type { Activity } from "../../../api/workItemActivities";
import { renderMarkdown } from "../../../utils/markdown";

export interface ActivityItemProps {
  activity: Activity;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function authorName(activity: Activity): string {
  if (activity.author.display_name !== null) {
    return activity.author.display_name;
  }
  if (activity.author.author_type === "agent") {
    return "Agent";
  }
  if (activity.author.author_type === "system") {
    return "System";
  }
  return "Unknown";
}

export const ActivityItem: Component<ActivityItemProps> = (props) => {
  return (
    <Switch>
      <Match when={props.activity.activity_type === "comment"}>
        <article
          data-testid="activity-item"
          data-variant="comment"
          class="rounded-md border border-line bg-surface p-3"
        >
          <header class="mb-2 flex items-center gap-2 text-sm">
            <span class="font-medium text-ink">
              {authorName(props.activity)}
            </span>
            <span class="text-ink-3">
              {formatTimestamp(props.activity.created_at)}
            </span>
          </header>
          <div
            data-testid="activity-body"
            class="chat-markdown text-sm text-ink-2"
            innerHTML={renderMarkdown(props.activity.body)}
          />
        </article>
      </Match>

      <Match when={props.activity.activity_type === "agent_update"}>
        <article
          data-testid="activity-item"
          data-variant="agent_update"
          class="rounded-md border border-signal-magenta/25 bg-signal-magenta-tint p-3"
        >
          <header class="mb-2 flex items-center gap-2 text-sm">
            <span class="rounded bg-surface px-1.5 py-0.5 text-xs font-medium text-signal-magenta">
              Agent
            </span>
            <span class="font-medium text-ink">
              {authorName(props.activity)}
            </span>
            <span class="text-ink-3">
              {formatTimestamp(props.activity.created_at)}
            </span>
          </header>
          <div
            data-testid="activity-body"
            class="chat-markdown text-sm text-ink-2"
            innerHTML={renderMarkdown(props.activity.body)}
          />
        </article>
      </Match>

      <Match when={props.activity.activity_type === "system_event"}>
        <div
          data-testid="activity-item"
          data-variant="system_event"
          class="flex items-center gap-2 px-2 py-1 text-xs text-ink-3"
        >
          <span aria-hidden="true">•</span>
          <span class="font-medium">{authorName(props.activity)}</span>
          <span>{props.activity.body}</span>
          <span class="text-ink-3">
            {formatTimestamp(props.activity.created_at)}
          </span>
        </div>
      </Match>
    </Switch>
  );
};
